import { put, call, all, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import i18next from 'i18next';

import * as actions from "../actions";

const isApproximatelyEqual = (a, b, epsilon = 0.001) => Math.abs((a || 0) - (b || 0)) < epsilon;

const perKiloItemHadExpressSurcharge = (item) => {
    const laundry = item && item.laundry;
    if (!laundry || !laundry.perKilo) return false;

    const quantity = item.itemQuantity || 1;
    if (quantity <= 0) return false;

    const basePrice = (laundry.washingPerKiloPrice > 0)
        ? laundry.washingPerKiloPrice
        : (laundry.washingAndIroningPerKiloPrice || 0);
    const speedyPrice = laundry.washingAndIroningSpeedyPrice || 0;
    if (speedyPrice <= 0) return false;

    const unitPrice = (item.sumPrice || 0) / quantity;
    return isApproximatelyEqual(unitPrice, basePrice + speedyPrice);
};

const reorderNeedsExpress = (orderItems = []) => {
    return (orderItems || []).some((item) => {
        const laundry = item && item.laundry;
        if (!laundry) return false;
        if (laundry.washingAndIroningSpeedySelected === true) return true;
        return perKiloItemHadExpressSurcharge(item);
    });
};

export function* GetProducts() {

    yield put(actions.getProductsStart());

    try {
        const response = yield call(fetch, '/api/products', {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept-Language": i18next.language.split('-')[0] === 'en' ? 'en-GB' : 'de-DE'
            }
        });

        const products = yield response.json();

        yield put(actions.getProductsSuccess(products));

    } catch (e) {
        yield put(actions.getProductsFail(e));
        return;
    }

}

export function* ReorderItems({ orderItems }) {
    yield put(actions.reorderItemsStart());

    try {
        const shouldEnableExpressForReorder = reorderNeedsExpress(orderItems);
        const isExpress = yield select(state => state.shoppingCart && state.shoppingCart.isExpress === true);
        if (isExpress) {
            yield put(actions.toggleExpress('reorderInit'));
        }
        if (shouldEnableExpressForReorder) {
            const isExpressAfterReset = yield select(state => state.shoppingCart && state.shoppingCart.isExpress === true);
            if (!isExpressAfterReset) {
                yield put(actions.toggleExpress('reorderRestore'));
            }
        }

        const requests = orderItems.map(item => call(fetch, `/api/products/${item._id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept-Language": i18next.language.split('-')[0] === 'en' ? 'en-GB' : 'de-DE'
            }
        }));

        const responses = yield all(requests);
        const failed = responses.find(r => !r.ok);
        if (failed) throw new Error('Some products could not be fetched');

        const products = yield all(responses.map(r => r.json()));

        for (let i = 0; i < products.length; i++) {
            const latest = products[i];
            const original = orderItems[i];

            const configured = {
                ...latest,
                // do not carry over any price fields; only set via actions below
                itemQuantity: original.itemQuantity || 1
            };

            // Set overlay product to the latest product first
            yield put(actions.showConfigurationModal(configured, [configured]));

            // Apply user-changeable configuration from original
            if (original.laundry && latest.laundry) {
                if (original.laundry.noLaundrySelected === true) {
                    yield put(actions.noLaundryCheck(0));
                } else if (original.laundry.washingAndManualIroningSelected === true && latest.laundry.washingAndManualIroningPrice !== undefined) {
                    yield put(actions.washingAndManualIroningCheck(0));
                } else if (original.laundry.washingAndIroningSpeedySelected === true && latest.laundry.washingAndIroningSpeedyPrice !== undefined) {
                    yield put(actions.washingAndIroningSpeedyCheck(0));
                } else if (original.laundry.washingAndIroningSelected === true && latest.laundry.washingAndIroningPrice !== undefined) {
                    yield put(actions.washingAndIroningCheck(0));
                }
            }

            if (original.tailoring && latest.tailoring) {
                if (original.tailoring.buttonSelected && latest.tailoring.buttonPrice !== undefined) {
                    yield put(actions.buttonCheck(0));
                    for (let c = 1; c < (original.tailoring.buttonQuantity || 1); c++) {
                        yield put(actions.quantityAdd(0, 'button'));
                    }
                }
                if (original.tailoring.hemSelected && latest.tailoring.hemPrice !== undefined) {
                    yield put(actions.hemCheck(0));
                    for (let c = 1; c < (original.tailoring.hemQuantity || 1); c++) {
                        yield put(actions.quantityAdd(0, 'hem'));
                    }
                }
                if (original.tailoring.zipperSelected && latest.tailoring.zipperPrice !== undefined) {
                    yield put(actions.zipperCheck(0));
                    for (let c = 1; c < (original.tailoring.zipperQuantity || 1); c++) {
                        yield put(actions.quantityAdd(0, 'zipper'));
                    }
                }
            }

            if (original.delivery && latest.delivery) {
                if (original.delivery.packedSelected && latest.delivery.packedPrice !== undefined) {
                    yield put(actions.deliveryCheck(0));
                } else if (original.delivery.hangerSelected && latest.delivery.hangerPrice !== undefined) {
                    // two toggles to target hanger if first toggle becomes packed
                    yield put(actions.deliveryCheck(0));
                    yield put(actions.deliveryCheck(0));
                }
            }

            if (original.additional && latest.additional) {
                const additionalKeys = ['decorationSelected','platedSelected','sequinedSelected','longSelected','rufflesSelected','impregnateSelected','chiffonSelected','bibSelected'];
                for (const key of additionalKeys) {
                    const priceKey = key.replace('Selected','Price');
                    if (original.additional[key] && (latest.additional[priceKey] !== undefined || latest.additional[key] !== undefined)) {
                        yield put(actions.additionalCheck(0, key));
                    }
                }
            }

            if (original.leather && latest.leather) {
                if (original.leather.napaSelected && latest.leather.napaPrice !== undefined) {
                    yield put(actions.leatherTypeCheck(0));
                } else if (original.leather.velourSelected && latest.leather.velourPrice !== undefined) {
                    yield put(actions.leatherTypeCheck(0));
                }
            }

            if (original.notes) {
                yield put(actions.notesChange(0, original.notes));
            }

            for (let q = 1; q < (original.itemQuantity || 1); q++) {
                yield put(actions.quantityAdd(0, 'item'));
            }

            // 8) Gift card fields (name, email, message)
            if (original.giftCard && (original.giftCard.name || original.giftCard.email || original.giftCard.message)) {
                const values = {
                    name: original.giftCard.name || '',
                    email: original.giftCard.email || '',
                    message: original.giftCard.message || ''
                };
                // submit into overlay item (index 0)
                yield put(actions.giftCartItemSubmit(values, 0));
            }

            // Finally commit the overlay item into the cart
            yield put(actions.AddToShoppingCart(configured));
        }

        yield put(actions.reorderItemsSuccess());
        // go to cart when reorder completed
        yield put(push('/shopping-cart/page1'));

    } catch (e) {
        yield put(actions.reorderItemsFail(e));
    }
}